import axios from "axios";

const BASE = process.env.REACT_APP_BACKEND_URL;

// Same-origin API calls avoid the ingress wildcard-CORS conflict; auth uses Bearer tokens.
const SAME_ORIGIN = typeof window !== "undefined" && BASE && !BASE.includes(window.location.hostname);

export const api = axios.create({ baseURL: SAME_ORIGIN ? "/api" : `${BASE}/api` });

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("aikyam_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

export function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export const errMsg = (e) => formatApiErrorDetail(e?.response?.data?.detail) || e.message;

export const CONF_LABELS = { 1: "General", 2: "Sensitive", 3: "Restricted" };

export const LOGO = "/aikyam-logo.png";

// Descriptions drawn verbatim from the Aikyam brochure — Three perspectives. One purpose.
export const fileUrl = (path) =>
  `${process.env.REACT_APP_BACKEND_URL}/api${path}${path.includes("?") ? "&" : "?"}token=${localStorage.getItem("aikyam_token") || ""}`;

export const FRAMEWORK_META = {
  dinacharya: {
    name: "Dinacharya",
    lens: "Your Wisdom",
    question: "How much of your wisdom is embedded in your business?",
    flow: "CEO → SLT",
    tagline: "Turning leadership intent into organisational accountability.",
    path: "dinacharya",
    accent: "teal",
    motto: "Excellence in Accountability",
    scoreLabel: "Team score",
    icon: "https://customer-assets-rejwkqb3.emergentagent.net/job_family-biz-hub-5/artifacts/8m881v47_WhatsApp%20Image%202026-08-27%20at%2013.17.59-2.jpeg",
  },
  panchatanthra: {
    name: "Panchatanthra",
    lens: "Your Business",
    question: "Is your business becoming valuable in the eyes of astute investors?",
    flow: "CEO → Chairman / Family",
    tagline: "Bringing performance, strategy and transformation into one meaningful conversation.",
    path: "panchatanthra",
    accent: "ember",
    motto: "Valuable Business Asset",
    scoreLabel: "Business score",
    icon: "https://customer-assets-rejwkqb3.emergentagent.net/job_family-biz-hub-5/artifacts/3g1u2enl_WhatsApp%20Image%202026-08-27%20at%2013.17.59.jpeg",
  },
  viveka_kartha: {
    name: "Viveka Kartha",
    lens: "Your Family",
    question: "How is your family preparing itself to embrace what you have built?",
    flow: "Chairman → Family & Wealth",
    tagline: "Stewarding ownership, legacy and family wealth across generations.",
    path: "viveka-kartha",
    accent: "plum",
    motto: "Invaluable Business Family",
    scoreLabel: "Legacy score",
    icon: "https://customer-assets-rejwkqb3.emergentagent.net/job_family-biz-hub-5/artifacts/subrfn1i_WhatsApp%20Image%202026-08-27%20at%2013.17.59-3.jpeg",
  },
};

export const ACCENT = {
  teal: {
    text: "text-teal",
    bg: "bg-teal",
    soft: "bg-gradient-to-br from-teal/18 via-teal/6 to-transparent",
    border: "border-teal/35",
    ring: "hover:border-teal",
    bar: "bg-gradient-to-r from-teal to-[hsl(186_68%_58%)]",
  },
  ember: {
    text: "text-[hsl(20_86%_42%)]",
    bg: "bg-terracotta",
    soft: "bg-gradient-to-br from-terracotta/18 via-gold/10 to-transparent",
    border: "border-terracotta/35",
    ring: "hover:border-terracotta",
    bar: "bg-gradient-to-r from-terracotta to-gold",
  },
  plum: {
    text: "text-plumink",
    bg: "bg-plum",
    soft: "bg-gradient-to-br from-plum/18 via-plum/6 to-transparent",
    border: "border-plum/35",
    ring: "hover:border-plum",
    bar: "bg-gradient-to-r from-plum to-[hsl(288_58%_62%)]",
  },
  deepblue: {
    text: "text-deepblue",
    bg: "bg-deepblue",
    soft: "bg-gradient-to-br from-deepblue/18 via-sky/10 to-transparent",
    border: "border-deepblue/30",
    ring: "hover:border-deepblue",
    bar: "bg-gradient-to-r from-navy via-deepblue to-[hsl(205_78%_62%)]",
  },
  gold: {
    text: "text-goldink",
    bg: "bg-gold",
    soft: "bg-gradient-to-br from-gold/22 via-sunny/10 to-transparent",
    border: "border-gold/40",
    ring: "hover:border-gold",
    bar: "bg-gradient-to-r from-gold to-sunny",
  },
};
